// Where a lead came from (plan P0-5, docs/ai-search-plan.md), captured
// automatically and sent with every lead form (/contact,
// /hire-an-individual, the service-page forms). Nobody is asked: the owner
// chose tracking only, no "How did you hear about us?" question (2026-10-29).
//
// Two copies of the source are kept:
//   1. Saved in localStorage for the forms ("last non-direct touch"): every
//      arrival with UTM tags, or from another site, replaces it; a direct
//      arrival (typed URL, bookmark) keeps it; it expires after MAX_AGE_DAYS.
//      So a visitor who lands from chatgpt.com today and comes back tomorrow
//      by typing the URL is still credited to ChatGPT.
//   2. Kept in the address bar for the rest of the visit (owner's request,
//      2026-10-29): the UTM tags the visit arrived with are put back on every
//      page it opens, so tools that read the page URL (the GoHighLevel chat
//      widget, the Zoom booking embed) see them too. Only for that visit
//      (sessionStorage), so a later direct visit isn't re-tagged.
// ChatGPT adds utm_source=chatgpt.com to its links itself.
// docs/lead-source-tracking.md has the GoHighLevel and GA4 set-up.

const KEY = 'ghlp_lead_source'
const VISIT_KEY = 'ghlp_visit_utm'
const MAX_AGE_DAYS = 90
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

// Friendly names for lead_source, matched against the UTM source or the
// referring domain. First match wins, so Gemini sits above Google.
const SOURCE_NAMES = [
  [/chatgpt|openai/, 'ChatGPT'],
  [/claude/, 'Claude'],
  [/perplexity/, 'Perplexity'],
  [/gemini/, 'Gemini'],
  [/copilot/, 'Copilot'],
  [/bing/, 'Bing'],
  [/google/, 'Google'],
  [/facebook|instagram|^fb$|^ig$/, 'Facebook / Instagram'],
  [/linkedin|lnkd/, 'LinkedIn'],
  [/youtube|youtu\.be/, 'YouTube'],
  [/reddit/, 'Reddit'],
  [/upwork/, 'Upwork'],
]

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

function sourceName(value) {
  const key = value.toLowerCase()
  const match = SOURCE_NAMES.find(([pattern]) => pattern.test(key))
  return match ? match[1] : value
}

function utmFrom(url) {
  const params = new URL(url).searchParams
  return Object.fromEntries(UTM_KEYS.map((key) => [key, params.get(key) || '']))
}

// The non-empty UTM tags as a query string, e.g. "utm_source=chatgpt.com".
function utmQuery(utm) {
  return new URLSearchParams(UTM_KEYS.filter((key) => utm[key]).map((key) => [key, utm[key]])).toString()
}

// Puts the visit's UTM tags back on a URL that has none. Uses the native
// History API, which Next.js syncs with its router: no reload, no new history
// entry, and the page's canonical URL is unchanged.
function keepUtmInAddressBar(query) {
  const url = new URL(window.location.href)
  for (const [key, value] of new URLSearchParams(query)) url.searchParams.set(key, value)
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

// The visit's referrer if it is another site. document.referrer doesn't
// change on client-side navigation, so it only counts on the first check
// after a full page load; later checks look at the URL's UTM tags only.
let referrerChecked = false
function externalReferrer() {
  if (referrerChecked) return ''
  referrerChecked = true
  const ref = document.referrer
  return ref && hostOf(ref) !== hostOf(window.location.href) ? ref : ''
}

function readSaved() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (saved && Date.now() - saved.at < MAX_AGE_DAYS * 864e5) return saved
  } catch {
    // Storage blocked or unreadable: treated as nothing saved.
  }
  return null
}

// Called by App.jsx on every page load and every route change.
export function captureLeadSource() {
  const utm = utmFrom(window.location.href)
  const query = utmQuery(utm)
  const referrer = externalReferrer()

  let visitQuery = null
  try {
    visitQuery = sessionStorage.getItem(VISIT_KEY)
    if (query) sessionStorage.setItem(VISIT_KEY, query)
  } catch {
    // Storage blocked: the tags just aren't carried to the next page.
  }

  // A page of this visit opened without the tags (an internal link, a typed
  // URL in the same tab): put them back, nothing new to save.
  if (!query && visitQuery && !referrer) {
    keepUtmInAddressBar(visitQuery)
    return
  }
  // Arrived from another site without tags: the old tags no longer apply.
  if (!query && referrer) {
    try {
      sessionStorage.removeItem(VISIT_KEY)
    } catch {
      // Storage blocked: nothing was kept.
    }
  }

  // A new tagged or referred arrival. The same tags again on a later page of
  // the same visit (a reload) don't count, so landing_page stays the first page.
  const newArrival = (query && query !== visitQuery) || referrer
  if (!newArrival) return
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...utm, referrer, page: window.location.href, at: Date.now() }))
  } catch {
    // Storage blocked (private mode, strict settings): forms fall back to the
    // current page in leadSourceFields().
  }
}

/** Payload keys describing where this lead came from. Browser only. */
export function leadSourceFields() {
  const saved = readSaved()
  const ref = document.referrer
  const current = {
    ...utmFrom(window.location.href),
    referrer: ref && hostOf(ref) !== hostOf(window.location.href) ? ref : '',
    page: window.location.href,
  }
  const touch = saved || current
  const source = touch.utm_source || hostOf(touch.referrer)
  return {
    // One readable value for the CRM: ChatGPT, Google, LinkedIn..., the raw
    // UTM source or referring domain when it isn't in the list, or Direct.
    lead_source: source ? sourceName(source) : 'Direct',
    utm_source: touch.utm_source,
    utm_medium: touch.utm_medium,
    utm_campaign: touch.utm_campaign,
    utm_term: touch.utm_term,
    utm_content: touch.utm_content,
    landing_referrer: touch.referrer,
    landing_page: touch.page,
  }
}
